import { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';
import { useCanvasContext } from '../contexts/CanvasContext';
import { updateModelPath } from '../utils/modelPath';
import { createGLTFLoader } from '../utils/gltfLoader';

// Global loading state to prevent duplicate loads
const loadingStates = new Map();
const loadingPromises = new Map();

export const useModelLoader = (modelPath, modelName) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelData, setModelData] = useState(null);
  const { registerModel, getModel, assetQuality } = useCanvasContext();
  const loaderRef = useRef();
  const hasLoadedRef = useRef(false);
  
  // Update model path based on quality setting
  const qualityAwarePath = updateModelPath(modelPath, assetQuality);

  useEffect(() => {
    // Prevent duplicate loading from the same hook instance
    if (hasLoadedRef.current) {
      return;
    }

    // Check if model is already loaded in global context
    const existingModel = getModel(modelName);
    if (existingModel && existingModel.loaded) {
        logger.model(`Model ${modelName} already loaded from cache`);
      setModelData(existingModel);
      setIsLoading(false);
      hasLoadedRef.current = true;
      return;
    }

    // Check if model is currently being loaded
    if (loadingStates.get(modelName) === 'loading') {
      const existingPromise = loadingPromises.get(modelName);
      if (existingPromise) {
        existingPromise.then((model) => {
          setModelData(model);
          setIsLoading(false);
          hasLoadedRef.current = true;
        }).catch((err) => {
          setError(err);
          setIsLoading(false);
          hasLoadedRef.current = true;
        });
        return;
      }
    }

    // Start loading the model
    setIsLoading(true);
    setError(null);
    loadingStates.set(modelName, 'loading');

    const loader = createGLTFLoader();
    loaderRef.current = loader;

    const loadPromise = new Promise((resolve, reject) => {
      logger.model(`Loading model ${modelName} with quality ${assetQuality} from ${qualityAwarePath}`);
      loader.load(
        qualityAwarePath,
        (gltf) => {
          logger.model(`Model ${modelName} loaded successfully`);
          
          // Process the model
          const processedModel = {
            gltf,
            scene: gltf.scene,
            name: modelName,
            path: qualityAwarePath,
            loaded: true,
            timestamp: Date.now()
          };

          // Register in global context
          registerModel(modelName, processedModel);
          
          loadingStates.set(modelName, 'loaded');
          loadingPromises.delete(modelName);
          resolve(processedModel);
        },
        (progress) => {
          // Only log progress for the first instance to avoid spam
          if (!loadingPromises.has(modelName)) {
            logger.model(`Loading ${modelName}:`, (progress.loaded / progress.total * 100) + '%');
          }
        },
        (error) => {
          console.error(`Error loading model ${modelName}:`, error);
          loadingStates.set(modelName, 'error');
          loadingPromises.delete(modelName);
          reject(error);
        }
      );
    });

    loadingPromises.set(modelName, loadPromise);

    loadPromise.then((model) => {
      setModelData(model);
      setIsLoading(false);
      hasLoadedRef.current = true;
    }).catch((err) => {
      setError(err);
      setIsLoading(false);
      hasLoadedRef.current = true;
    });

    return () => {
      // Cleanup if component unmounts during loading
      if (loaderRef.current) {
        loaderRef.current = null;
      }
    };
  }, [qualityAwarePath, modelName, registerModel, getModel, assetQuality]);

  return {
    modelData,
    isLoading,
    error,
    gltf: modelData?.gltf,
    scene: modelData?.scene
  };
};

